import { useIntl } from 'react-intl';

import type { ContentfulImageWithFocalPointFragment } from '@/graphql/contentful/api';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useRouter } from '@/hooks/useRouter';
import type { StoreHit } from '@/integrations/algolia/types';
import { getGoogleMapAddress } from '@/lib/boutique/helpers';
import { EVENT_NAME } from '@/lib/gtm/const';
import { paths } from '@/lib/paths';
import { messages as boutiqueMessages } from '@/messages/boutique';
import { commonMessages } from '@/messages/common';

import type { ExhibitionItem } from '../../ExhibitionList';
import { formatDate } from '../../helper';

import * as S from './UpcomingExhibitionItem.styles';
export type UpcomingExhibitionItemType = {
  boutique?: (StoreHit & ExhibitionItem) | null;
  imagePosition: 'left' | 'right';
};
export const UpcomingExhibitionItem = ({
  boutique,
  imagePosition,
}: UpcomingExhibitionItemType) => {
  const { push } = useRouter();
  const { formatMessage } = useIntl();
  const { sendEvent } = useGoogleAnalytics();
  if (!boutique) {
    return null;
  }
  const handleDirectToAppointment = () => {
    if (boutique?.slug) {
      sendEvent({
        event: EVENT_NAME.INTENTION_BOOK_APPT,
      });

      push(
        paths.stores.appointment.asPath({
          country: boutique?.country_code.toLowerCase(),
          slug: boutique?.slug,
        }),
      );
    }
  };
  const emailHref = `mailto:${boutique?.emails?.[0]}`;

  return (
    <S.StyledContainer>
      <S.ExhibitionOuter>
        <S.ExhibitionWraper $imagePosition={imagePosition}>
          <S.ExhibitionImageWraper $imagePosition={imagePosition}>
            {boutique?.media ? (
              <S.StyledMediaWrapper
                {...(boutique?.media as ContentfulImageWithFocalPointFragment)}
                aspectRatio={{ x: 42, y: 25 }}
                data-test-id='BaseCardTypeBackgroundImage'
                preload={true}
                sizes='(max-width: 600px) 50vw, 20vw'
                useAspectRatio={true}
              />
            ) : (
              <S.StyledExhibitionImage
                alt='aniversary image'
                aspectRatio={{ x: 42, y: 25 }}
                focalPoint={{
                  x: 960,
                  y: 262,
                }}
                loaderProps={{ height: 240 }}
                loading='eager'
                originalImageDimensions={undefined}
                showFallbackPlaceholder
                sizes='(max-width: 600px) 50vw, 20vw'
                src={boutique?.thumbnail_url ?? ''}
                useLoader
                width='100%'
              />
            )}
          </S.ExhibitionImageWraper>
          <S.ExhibitionCard $imagePosition={imagePosition}>
            <S.ExhibitionCardContent>
              <S.TitlesWrapper>
                <S.CityTitle
                  $imagePosition={imagePosition}
                  color='primary'
                  fontFamily='italianPlate'
                  textTransform='uppercase'
                >
                  {boutique?.label ?? boutique?.city}
                </S.CityTitle>
                <S.DateTitle
                  $imagePosition={imagePosition}
                  color='default'
                  fontFamily='italianPlate'
                  textTransform='uppercase'
                >
                  {boutique?.startDate && formatDate(boutique.startDate)}
                </S.DateTitle>
              </S.TitlesWrapper>
              <S.BoutiqueDetailWrapper>
                <S.BoutiqueDetailInnerWrapper>
                  <S.BoutiqueName textTransform='uppercase'>
                    {boutique?.name}
                  </S.BoutiqueName>
                  <S.ContactLine textTransform='capitalize'>
                    <S.LocationIcon />
                    {boutique?.street?.toLowerCase()}
                  </S.ContactLine>
                  <S.ContactLine>
                    <S.PhoneOutlineIcon />
                    {boutique?.phone}
                  </S.ContactLine>
                  <S.ExhibitionCtaWrapper>
                    <S.LinksWrapper>
                      <S.EmailDirectionLinksWrapper>
                        {emailHref && (
                          <S.BoutiqueLink
                            href={emailHref}
                            isUpperCase={false}
                            label={formatMessage(boutiqueMessages.email)}
                            variant='link'
                          >
                            <S.Chevron />
                          </S.BoutiqueLink>
                        )}
                        {boutique?._geoloc?.lat && (
                          <S.BoutiqueLink
                            href={getGoogleMapAddress(boutique?._geoloc)}
                            isUpperCase={false}
                            label={formatMessage(
                              boutiqueMessages.getDirections,
                            )}
                            onClick={() =>
                              sendEvent({
                                event: EVENT_NAME.LOCATOR_PIN_DETAILS,
                                retailer_name: boutique?.name,
                              })
                            }
                            rel='noopener noreferrer'
                            target='_blank'
                            variant='link'
                          >
                            <S.Chevron />
                          </S.BoutiqueLink>
                        )}
                      </S.EmailDirectionLinksWrapper>
                      <S.AppointmentCtaDeskTop
                        colorVariant='blue'
                        onClick={handleDirectToAppointment}
                        size='s'
                        variant='outlined'
                      >
                        {formatMessage(commonMessages.bookAppointment)}
                      </S.AppointmentCtaDeskTop>
                    </S.LinksWrapper>

                    <S.AppointmentCtaMobileWrapper>
                      {' '}
                      <S.AppointmentCtaMobile
                        colorVariant='blue'
                        onClick={handleDirectToAppointment}
                        variant='outlined'
                      >
                        {formatMessage(commonMessages.bookAppointment)}
                      </S.AppointmentCtaMobile>
                    </S.AppointmentCtaMobileWrapper>
                  </S.ExhibitionCtaWrapper>
                </S.BoutiqueDetailInnerWrapper>
              </S.BoutiqueDetailWrapper>
              <S.ExhibitionWrapper>
                <S.CalendarIcon />
                <S.ExhibitionPeriod>
                  {formatMessage(commonMessages.from)}{' '}
                  <S.ExhibitionPeriodSpan>
                    {boutique?.startDate && formatDate(boutique.startDate)}
                  </S.ExhibitionPeriodSpan>{' '}
                  {formatMessage(commonMessages.to).toLowerCase()}{' '}
                  <S.ExhibitionPeriodSpan>
                    {boutique?.endDate && formatDate(boutique.endDate)}
                  </S.ExhibitionPeriodSpan>
                </S.ExhibitionPeriod>
              </S.ExhibitionWrapper>
            </S.ExhibitionCardContent>
          </S.ExhibitionCard>
        </S.ExhibitionWraper>
      </S.ExhibitionOuter>
    </S.StyledContainer>
  );
};
